import React, { useEffect, useRef } from 'react';
import { Stack, Text } from 'braid-design-system';

const FOCUSABLE_SELECTOR = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

export interface PriceChangeEntry {
  date: string | null;
  new_selling_price: number | null;
}

interface PriceChangeModalProps {
  isOpen: boolean;
  itemName: string;
  itemCode: string;
  priceChanges: PriceChangeEntry[];
  loading: boolean;
  error: string | null;
  onClose: () => void;
  onSelectPrice?: (price: number) => void;
}

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
};

const formatPrice = (price: number | null): string => {
  if (price === null || price === undefined) return '-';
  return Number(price).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const PriceChangeModal: React.FC<PriceChangeModalProps> = ({
  isOpen,
  itemName,
  itemCode,
  priceChanges,
  loading,
  error,
  onClose,
  onSelectPrice,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const container = modalRef.current;
    if (!container) {
      return undefined;
    }

    const getFocusable = () =>
      Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (el) => !el.hasAttribute('disabled')
      );

    const focusable = getFocusable();
    (focusable[0] || container).focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') {
        return;
      }

      const elements = getFocusable();
      if (elements.length === 0) {
        return;
      }

      const first = elements[0];
      const last = elements[elements.length - 1];
      const activeElement = document.activeElement;

      if (event.shiftKey) {
        if (activeElement === first || !container.contains(activeElement)) {
          event.preventDefault();
          last.focus();
        }
      } else if (activeElement === last || !container.contains(activeElement)) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen, priceChanges]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="search-modal-overlay" onClick={onClose}>
      <div className="price-change-modal" ref={modalRef} tabIndex={-1} onClick={(e) => e.stopPropagation()}>
        <div className="search-modal-header">
          <Stack space="xsmall">
            <Text weight="strong" size="large">
              Price History
            </Text>
            <Text tone="secondary" size="small">
              {itemName} ({itemCode})
            </Text>
          </Stack>
          <button type="button" onClick={onClose} className="search-modal-close">
            ✕
          </button>
        </div>

        <div className="search-modal-content">
          {loading && (
            <div className="price-change-loading">
              <Text tone="secondary">Loading price history...</Text>
            </div>
          )}

          {error && (
            <div className="price-change-error">
              <Text tone="critical">{error}</Text>
            </div>
          )}

          {!loading && !error && priceChanges.length === 0 && (
            <div className="price-change-empty">
              <Text tone="secondary">No price changes found for this item.</Text>
            </div>
          )}

          {!loading && !error && priceChanges.length > 0 && (
            <table className="price-change-table">
              <thead>
                <tr>
                  <th>Date Changed</th>
                  <th className="price-col-right">New Selling Price</th>
                </tr>
              </thead>
              <tbody>
                {priceChanges.map((change, index) => {
                  const isSelectable = Boolean(
                    onSelectPrice && change.new_selling_price !== null && change.new_selling_price !== undefined
                  );
                  const selectRow = () => {
                    if (isSelectable) {
                      onSelectPrice!(change.new_selling_price as number);
                      onClose();
                    }
                  };

                  return (
                    <tr
                      key={index}
                      className={isSelectable ? 'price-change-row-clickable' : ''}
                      onClick={selectRow}
                      {...(isSelectable
                        ? {
                            tabIndex: 0,
                            role: 'button',
                            onKeyDown: (event: React.KeyboardEvent<HTMLTableRowElement>) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                selectRow();
                              }
                            },
                          }
                        : {})}
                    >
                      <td>{formatDate(change.date)}</td>
                      <td className="price-col-right">{formatPrice(change.new_selling_price)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default PriceChangeModal;